import {
  createFileRoute,
  notFound,
  Outlet,
  redirect,
} from "@tanstack/react-router"
import { GatedSessionDetailsFailReason } from "ipctg-inflow-types/InFlowEnums"

import api from "@/utilities/api"

import { Route as QrQrLookupIDGatedPamIDLookUpChallengeImport } from "@/routes/qr/$qrLookupID/gated/$pamID/look-up.challenge"
import { Route as QrQrLookupIDGatedPamIDLookUpIndexImport } from "@/routes/qr/$qrLookupID/gated/$pamID/look-up.index"
import { matchesToRoutes } from "@/utilities/etc"
import { ExpectedError } from "@/utilities/expectedErrors"
import { NOT_FOUND_SESSION } from "@/utilities/globals"
import { gatedSessionDetailsQueryOptions } from "@/utilities/hooks/queries/gated"
import { log } from "@/utilities/logging"

export const Route = createFileRoute("/qr/$qrLookupID/gated/$pamID/$sessionID")(
  {
    beforeLoad: async ({ context, params, matches }) => {
      /*
        The welcome token is fetched on the destination page (cart/lookup)
        because it was getting lost across the redirect. Arriving at the cart
        from the license or phone lookups, we already have one.
      */

      const isSessionRootPage = matchesToRoutes({
        matches,
        paths: ["/qr/$qrLookupID/gated/$pamID/$sessionID/"],
      })

      // No token on the index page yet, so send them to the cart, which gets one
      if (isSessionRootPage) {
        log({
          level: "warn",
          flow: "gated",
          scope: "guard.sessionRoot",
          message: "On the session index, redirecting to /cart",
          meta: { sessionID: params.sessionID },
        })
        throw redirect({
          to: "/qr/$qrLookupID/gated/$pamID/$sessionID/cart",
          params,
          search: { from: "/qr/$qrLookupID/gated/$pamID/$sessionID/" },
        })
      }

      const cartPageMatch = matches.find(
        (m) => m.routeId === "/qr/$qrLookupID/gated/$pamID/$sessionID/cart"
      )

      if (
        cartPageMatch &&
        ![
          QrQrLookupIDGatedPamIDLookUpIndexImport.id,
          QrQrLookupIDGatedPamIDLookUpChallengeImport.id,
        ].some((id) => id === cartPageMatch.search.from)
      ) {
        await api.welcome.POST({ qr: params.qrLookupID })
      }

      try {
        const sessionDetails = await context.queryClient.fetchQuery(
          gatedSessionDetailsQueryOptions({ sessionID: params.sessionID })
        )

        return { sessionDetails }
      } catch (error) {
        if (
          error instanceof ExpectedError &&
          error.failReason === GatedSessionDetailsFailReason.SessionNotFound
        ) {
          log({
            level: "warn",
            flow: "gated",
            scope: "guard.session",
            message: "Session not found, showing not-found page",
            meta: { sessionID: params.sessionID },
          })
          throw notFound({
            data: {
              type: NOT_FOUND_SESSION,
            },
          })
        }
        log({
          level: "error",
          flow: "gated",
          scope: "guard.session",
          message: "Failed to load session details",
          meta: { sessionID: params.sessionID, error },
        })
        throw error
      }
    },
    shouldReload: true,
    component: Outlet,
  }
)
